import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { mdiInformationOutline, mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";

import Button from "components/Button";
import Toggle from "components/Toggle";
import Tooltip from "components/Tooltip";
import { showSuccessMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { NotificationsFormData } from "models/UserSetting";
import { AppDispatch, RootState } from "store";
import { updateUserProfile } from "store/user/actions";
import styles from "./style.module.scss";

export default function MessageSettingsForm() {
  const dispatch = useDispatch<AppDispatch>();
  const { isDesktop } = useDeviceType();
  const { userDetails } = useSelector((state: RootState) => state.userState);
  const { account } = useSelector((state: RootState) => state.authState);

  useEffect(() => {
    reset({
      enable_sms_alerts: userDetails?.enable_sms_alerts
    });
  }, [userDetails]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm<Partial<NotificationsFormData>>({
    defaultValues: {
      enable_sms_alerts: userDetails?.enable_sms_alerts
    }
  });

  const onSubmit = async ({ enable_sms_alerts }: { enable_sms_alerts: boolean }) => {
    const formData = {
      type: account.type as string,
      userId: account[account.type as keyof typeof account] as string,
      enable_sms_alerts
    } as any;
    await dispatch(updateUserProfile(formData)).then(() => {
      showSuccessMessage("Settings updated successfully.");
    });
  };

  return (
    <form className={styles.messageForm} onSubmit={handleSubmit(onSubmit)}>
      <div className={styles.textNotifications}>
        <p className={clsx(styles.header, "!pb-4")}>Text Message Notifications</p>
        <div className={styles.textMessageBody}>
          <div className={styles.enableTextNotifications}>
            <span className={clsx(styles.tableRow, "!flex-none")}>Enable Text Notifications</span>
            <Tooltip
              position="center"
              message={
                <div className={styles.tooltipWrapper}>
                  <p>
                    If enabled, we’ll send you a text(SMS) when there are urgent matters that need
                    your attention, such as:
                  </p>
                  <ul className={styles.list}>
                    <li>There are document issues with a funding request</li>
                    <li>You are underbid a bid</li>
                    <li>And others</li>
                  </ul>
                  <p>
                    Depending on your activity on our platform, you can expect between 1 text every
                    month to 1 text per day. Charges my apply.
                  </p>
                </div>
              }>
              <Icon path={mdiInformationOutline} className="size-4 desktop:size-5" />
            </Tooltip>
          </div>

          {userDetails && (
            <Toggle
              name="enable_sms_alerts"
              register={register}
              bgColor="before:bg-success"
              className="mr-4"
            />
          )}
        </div>
      </div>

      <Button
        type="submit"
        variant={isDesktop ? "outline" : "contained"}
        className={styles.submit}
        disabled={!userDetails?.has_verified_email || isSubmitting}>
        {isSubmitting ? <Icon path={mdiLoading} size={1.5} spin={1} /> : "Save"}
      </Button>
    </form>
  );
}
